/**
 * Provides a hook to manage the range data for a timeseries.
 * 
 * Manages the range data for a timeseries: default, loaded, and visible.
 * - default: the default range from the config
 * - loaded: the range of data that is loaded into memory
 * - visible: the range of data that is visible in the plot
 * 
 * Manages loading high-resolution data when the default range is changed or the plot is zoomed in.
 * Manages merging the high-resolution data with the low-resolution data when the plot is zoomed out through {@link useDataMerge}
 * Manages refreshing the data when the plot is refreshed.
 * 
 * @packageDocumentation
 * @module useTimeseriesRangeData
 * @public
 */

import type { IntervalJSON } from '@highimpact-frontend/api-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { buildRequestKey } from './timeseriesRange/buildRequestKey';
import { DEFAULT_DECIMATION_POLICY } from './timeseriesRange/const';
import {
  resolveDefaultBounds,
  shouldExtendVisibleRangeOnRefresh,
  snapshotCoversRange,
  type RangeBounds,
} from './timeseriesRange/resolveRangeBounds';
import type {
  CurveDataRole,
  DecimationPolicy,
  TimeseriesDataTransport,
  TimeseriesFetchRequestWithCache
} from './timeseriesRange/types';
import {
  assessRelationship,
  computeVisibleDataBeginIndex,
  decideStrategy,
  executeMerge,
  type MergeConfig,
  type MergeDecision,
  type RangeAssessment,
} from './useDataMerge';
// used for @link documentation: no runtime impact
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { useSessionCacheTransport } from './timeseriesRange/useSessionCacheTransport';

// -- type definitions --

/** Interface for the options for the useTimeseriesRangeData hook. */
export interface UseTimeseriesRangeDataOptions<TRow extends object> {
  defaultRange: IntervalJSON[];
  transport: TimeseriesDataTransport<TRow>;
  getTimestamp: (row: TRow) => string;
  decimationPolicy?: DecimationPolicy;
  rangeType?: 'relative' | 'calendar' | 'fixed' | null;
  curveRoles?: Record<string, CurveDataRole>;
}

/** Interface for the result of the useTimeseriesRangeData hook. */
export interface UseTimeseriesRangeDataResult<TRow extends object> {
  data: TRow[] | null;
  defaultRange: IntervalJSON[];
  loadedRange: RangeBounds | null;
  visibleRange: RangeBounds | null;
  /**
   * Index in {@link data} where the latest incoming API batch begins.
   * Used to rebase threshold `row_index` values from the last fetch response.
   */
  visibleDataBeginIndex: number;
  loading: boolean;
  error: Error | null;
  setDefaultRange: (ranges: IntervalJSON[]) => void;
  applyVisibleRangeChange: (start: string, end: string) => Promise<void>;
  applyPredefinedRange: (bounds: RangeBounds) => Promise<void>;
  refresh: (nowMs?: number) => Promise<void>;
  resetToDefault: () => void;
}

// -- helpers --

/** Build the overview request. */
function buildOverviewRequest(
  bounds: RangeBounds,
  decimationPolicy: DecimationPolicy,
  replace: boolean
): TimeseriesFetchRequestWithCache {
  const timeRange = { start: bounds.start, end: bounds.end };

  return {
    type: 'with-cache',
    slot: 'overview',
    timeRange,
    decimation: decimationPolicy,
    replace,
    requestKey: buildRequestKey('overview', timeRange, decimationPolicy),
  };
}

/** Build the detail request for a visible window. */
function buildDetailRequest(
  bounds: RangeBounds,
  decimationPolicy: DecimationPolicy
): TimeseriesFetchRequestWithCache {
  const timeRange = { start: bounds.start, end: bounds.end };

  return {
    type: 'with-cache',
    slot: 'detail',
    timeRange,
    decimation: decimationPolicy,
    replace: false,
    requestKey: buildRequestKey('detail', timeRange, decimationPolicy),
  };
}

/** Build the refresh request for an incremental append window. */
function buildRefreshRequest(
  bounds: RangeBounds,
  decimationPolicy: DecimationPolicy
): TimeseriesFetchRequestWithCache {
  const timeRange = { start: bounds.start, end: bounds.end };

  return {
    type: 'with-cache',
    slot: 'refresh',
    timeRange,
    decimation: decimationPolicy,
    replace: false,
    requestKey: buildRequestKey('refresh', timeRange, decimationPolicy),
  };
}

/** Compare ISO timestamps for merge and range assessment. */
function compareIso(a: string, b: string): number {
  return new Date(a).getTime() - new Date(b).getTime();
}

/** 
 * Update loaded bounds after a visible-range merge fetch.
 * 
 * - Never shrinks on zoom-in (H_SUBSET_O / H_EQUALS_O).
 * - Replace on zoom-out or panning completely away from the loaded range (H_SUPERSET_O / H_DISTINCT_O).
 * - Merge on zoom-out or panning partially away from the loaded range (H_OVERLAP_O).
 */
export function updateLoadedRangeFromAssessment(
  loadedRange: RangeBounds,
  visibleRange: RangeBounds,
  assessment: RangeAssessment
): RangeBounds {
  switch (assessment) {
    case 'H_SUBSET_O':
    case 'H_EQUALS_O':
      return loadedRange;
    case 'H_SUPERSET_O':
    case 'H_DISTINCT_O':
      return visibleRange;
    case 'H_OVERLAP_O':
      return {
        start:
          compareIso(visibleRange.start, loadedRange.start) < 0
            ? visibleRange.start
            : loadedRange.start,
        end:
          compareIso(visibleRange.end, loadedRange.end) > 0
            ? visibleRange.end
            : loadedRange.end,
      };
  }
}

// -- main exports --

/** 
 * Hook for handling timeseries data with a default range.
 * 
 * Uses a data transport layer to fetch the data from the API:
 * {@link useSessionCacheTransport} by default,
 * but can be replaced with any transport that implements the {@link TimeseriesDataTransport} interface.
 * 
 * @param options - The options for the useTimeseriesRangeData hook.
 * @returns The result of the useTimeseriesRangeData hook.
 * @example
 * const { 
 *   data, 
 *   defaultRange, 
 *   loadedRange, 
 *   visibleRange, 
 *   loading, 
 *   error, 
 *   setDefaultRange, 
 *   resetToDefault 
 * } = useTimeseriesRangeData({
 *   defaultRange: [{ start: '2026-01-01', end: '2026-01-02' }],
 *   transport: useSessionCacheTransport({
 *     mainUrl: '/api/some-api',
 *     mainBody: { 
 *       variant: 'python_only',
 *       contexts: [{ measure_point_id: 1, measure_type_id: 1 }],
 *       context_index_to_name: { '0': '0' },
 *       pipeline: { functions: [] },
 *     },
 *     tokenKey: 'security_token',
 *     cacheLimit: 20,
 *   }),
 *   getTimestamp: (row) => row.horodate,
 * });
 * 
 * // set the default range and fetch the data
 * setDefaultRange([{ start: '2026-01-01', end: '2026-01-02' }]);
 */
export function useTimeseriesRangeData<TRow extends object>(
  options: UseTimeseriesRangeDataOptions<TRow>
): UseTimeseriesRangeDataResult<TRow> {
  const {
    defaultRange: initialDefaultRange,
    transport,
    getTimestamp,
    decimationPolicy = DEFAULT_DECIMATION_POLICY,
    rangeType = null,
  } = options;

  // -- state --

  // -- public state --

  const [defaultRange, setDefaultRangeState] =
    useState<IntervalJSON[]>(initialDefaultRange);
  const [loadedRange, setLoadedRange] = useState<RangeBounds | null>(() =>
    resolveDefaultBounds(initialDefaultRange)
  );
  const [visibleRange, setVisibleRange] = useState<RangeBounds | null>(() =>
    resolveDefaultBounds(initialDefaultRange)
  );
  const [data, setData] = useState<TRow[] | null>(() =>
    transport.getSnapshot()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [visibleDataBeginIndex, setVisibleDataBeginIndex] = useState(0);

  // -- private state --

  /** ref of the getTimestamp function */
  const getTimestampRef = useRef(getTimestamp);
  getTimestampRef.current = getTimestamp;

  /** ref of the decimation policy */
  const decimationPolicyRef = useRef(decimationPolicy);
  decimationPolicyRef.current = decimationPolicy;

  /** ref of the range type */
  const rangeTypeRef = useRef(rangeType);
  rangeTypeRef.current = rangeType;

  /** ref of the initial default range */
  const initialDefaultRangeRef = useRef(initialDefaultRange);

  /** 
   * ref of the loaded range 
   * 
   * Used for stable callbacks and fresh reads after await.
   * 
   * Not an unnecessary duplication of the state:
   *  - gives latest value in callbacks at execution time
   *  - avoids re-rendering the component when the state changes
   */
  const loadedRangeRef = useRef(loadedRange);
  loadedRangeRef.current = loadedRange;
  /** 
   * ref of the visible range 
   * 
   * Not a duplicate: exists for the same reason as {@link loadedRangeRef}.
   */
  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;
  /** ref of the data 
   * 
   * Not a duplicate: exists for the same reason as {@link loadedRangeRef}.
   */
  const dataRef = useRef(data);
  dataRef.current = data;

  const mergeConfig = useRef<MergeConfig<TRow, string>>({
    getCoord: (row) => getTimestampRef.current(row),
    compare: compareIso,
  });

  const resolveVisibleDataBeginIndex = useCallback(
    (
      merged: readonly TRow[],
      incoming: readonly TRow[],
      decision: MergeDecision
    ): number => {
      if (decision === 'REPLACE_WITH_H') {
        return 0;
      }

      return computeVisibleDataBeginIndex(
        merged,
        incoming,
        (row) => getTimestampRef.current(row)
      );
    },
    []
  );

  /** Fetch through the transport while tracking hook-local loading and error state. */
  const runTransportFetch = useCallback(
    async (request: TimeseriesFetchRequestWithCache): Promise<TRow[]> => {
      const inflight = transport.getInflightRequest?.(request.requestKey);
      const fetchPromise = inflight ?? transport.fetch(request);

      setLoading(true);
      setError(null);

      try {
        return await fetchPromise;
      } catch (err) {
        const nextError =
          err instanceof Error ? err : new Error(String(err));
        setError(nextError);
        throw nextError;
      } finally {
        setLoading(false);
      }
    },
    [transport]
  );

  // -- callbacks --

  /** Adopt the rows into the state.
   * 
   * Helper for {@link loadOverview},
   * {@link loadOverviewForBounds},
   * and {@link initializeFromTransport}.
  */
  const adoptRows = useCallback(
    (rows: TRow[], bounds: RangeBounds, syncTransport: boolean) => {
      setData(rows);
      setLoadedRange(bounds);
      setVisibleRange(bounds);
      setVisibleDataBeginIndex(0);

      if (syncTransport) {
        transport.setMergedData(rows);
      }
    },
    [transport]
  );

  /** Load the overview data.
   * 
   * Helper for {@link initializeFromTransport},
   * {@link setDefaultRange},
   * and {@link resetToDefault}.
  */
  const loadOverview = useCallback(
    async (ranges: IntervalJSON[], replace: boolean) => {
      const bounds = resolveDefaultBounds(ranges);
      if (!bounds) {
        setData(null);
        setLoadedRange(null);
        setVisibleRange(null);
        setVisibleDataBeginIndex(0);
        return;
      }

      const request = buildOverviewRequest(
        bounds,
        decimationPolicyRef.current,
        replace
      );
      const rows = await runTransportFetch(request);

      adoptRows(rows, bounds, true);
    },
    [adoptRows, runTransportFetch]
  );

  /**
   * Load overview data for a resolved bounds window.
   * 
   * Helper for {@link applyPredefinedRange}.
   */
  const loadOverviewForBounds = useCallback(
    async (bounds: RangeBounds) => {
      const request = buildOverviewRequest(
        bounds,
        decimationPolicyRef.current,
        true
      );
      const rows = await runTransportFetch(request);

      adoptRows(rows, bounds, true);
    },
    [adoptRows, runTransportFetch]
  );

  /** Initialize the data from the transport.
   * 
   * Used in a useEffect on mount.
   */
  const initializeFromTransport = useCallback(
    async (ranges: IntervalJSON[]) => {
      const bounds = resolveDefaultBounds(ranges);
      if (!bounds) {
        return;
      }

      const request = buildOverviewRequest(
        bounds,
        decimationPolicyRef.current,
        true
      );
      const inflight = transport.getInflightRequest?.(request.requestKey);
      if (inflight) {
        const rows = await runTransportFetch(request);
        adoptRows(rows, bounds, false);
        return;
      }

      const snapshot = transport.getSnapshot();
      if (
        snapshotCoversRange(snapshot, getTimestampRef.current, bounds)
      ) {
        adoptRows(snapshot ?? [], bounds, false);
        return;
      }

      await loadOverview(ranges, true);
    },
    [adoptRows, loadOverview, runTransportFetch, transport]
  );

  const hasInitializedRef = useRef(false);

  // -- effects --

  /** Initialize the data from the transport on mount. */
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    let cancelled = false;

    void initializeFromTransport(initialDefaultRangeRef.current).catch(() => {
      if (!cancelled) {
        // runTransportFetch already records fetch failures in hook state
      }
    });

    return () => {
      cancelled = true;
    };
  }, [initializeFromTransport]);

  // -- public API methods --

  /** Set the default range and load the data. */
  const setDefaultRange = useCallback(
    (ranges: IntervalJSON[]) => {
      if (!Array.isArray(ranges)) {
        return;
      }

      setDefaultRangeState(ranges);
      const bounds = resolveDefaultBounds(ranges);

      if (!bounds) {
        setData(null);
        setLoadedRange(null);
        setVisibleRange(null);
        setVisibleDataBeginIndex(0);
        return;
      }

      setLoadedRange(bounds);
      setVisibleRange(bounds);
      void loadOverview(ranges, true);
    },
    [loadOverview]
  );

  /** Reset the range to the default range. */
  const resetToDefault = useCallback(() => {
    setDefaultRange(defaultRange);
  }, [defaultRange, setDefaultRange]);

  /** Apply a visible-range change from plot zoom/pan and merge detail data when needed. */
  const applyVisibleRangeChange = useCallback(
    async (start: string, end: string) => {
      const visibleBounds = { start, end };
      setVisibleRange(visibleBounds);

      const currentLoaded = loadedRangeRef.current;
      if (!currentLoaded) {
        return;
      }

      const assessment = assessRelationship(
        [currentLoaded.start, currentLoaded.end],
        [start, end],
        compareIso
      );

      if (!decimationPolicyRef.current.enabled && assessment === 'H_SUBSET_O') {
        return;
      }

      const decision = decideStrategy(assessment);
      if (decision === 'KEEP_O') {
        return;
      }

      const request = buildDetailRequest(
        visibleBounds,
        decimationPolicyRef.current
      );
      const incoming = await runTransportFetch(request);

      const snapshot = dataRef.current ?? transport.getSnapshot() ?? [];
      const merged = executeMerge(
        snapshot,
        incoming,
        [start, end],
        decision,
        mergeConfig.current
      );
      const nextLoaded = updateLoadedRangeFromAssessment(
        currentLoaded,
        visibleBounds,
        assessment
      );

      setData(merged);
      setLoadedRange(nextLoaded);
      setVisibleDataBeginIndex(
        resolveVisibleDataBeginIndex(merged, incoming, decision)
      );
      transport.setMergedData(merged);
    },
    [resolveVisibleDataBeginIndex, runTransportFetch, transport]
  );

  /** Apply a predefined relative pill range using the same merge policy as zoom. */
  const applyPredefinedRange = useCallback(
    async (bounds: RangeBounds) => {
      if (rangeTypeRef.current !== 'relative') {
        return;
      }

      const currentLoaded = loadedRangeRef.current;
      if (!currentLoaded) {
        await loadOverviewForBounds(bounds);
        return;
      }

      const assessment = assessRelationship(
        [currentLoaded.start, currentLoaded.end],
        [bounds.start, bounds.end],
        compareIso
      );

      if (assessment === 'H_DISTINCT_O' || assessment === 'H_SUPERSET_O') {
        await loadOverviewForBounds(bounds);
        return;
      }

      await applyVisibleRangeChange(bounds.start, bounds.end);
    },
    [applyVisibleRangeChange, loadOverviewForBounds]
  );

  /** Append incremental data from the loaded upper bound through now. */
  const refresh = useCallback(
    async (nowMs: number = Date.now()) => {
      const loaded = loadedRangeRef.current;
      const visible = visibleRangeRef.current;
      if (!loaded || !visible) {
        return;
      }

      const nowIso = new Date(nowMs).toISOString();
      if (compareIso(loaded.end, nowIso) >= 0) {
        return;
      }

      const extendVisible = shouldExtendVisibleRangeOnRefresh(
        visible,
        loaded,
        nowMs
      );
      const refreshBounds = { start: loaded.end, end: nowIso };
      const request = buildRefreshRequest(
        refreshBounds,
        decimationPolicyRef.current
      );
      const incoming = await runTransportFetch(request);

      const snapshot = dataRef.current ?? transport.getSnapshot() ?? [];
      const assessment = assessRelationship(
        [loaded.start, loaded.end],
        [refreshBounds.start, refreshBounds.end],
        compareIso
      );
      const decision = decideStrategy(assessment);
      const merged = executeMerge(
        snapshot,
        incoming,
        [refreshBounds.start, refreshBounds.end],
        decision,
        mergeConfig.current
      );
      const nextLoaded = { start: loaded.start, end: nowIso };
      const nextVisible = extendVisible
        ? { start: visible.start, end: nowIso }
        : visible;

      setData(merged);
      setLoadedRange(nextLoaded);
      setVisibleRange(nextVisible);
      setVisibleDataBeginIndex(
        resolveVisibleDataBeginIndex(merged, incoming, decision)
      );
      transport.setMergedData(merged);
    },
    [resolveVisibleDataBeginIndex, runTransportFetch, transport]
  );

  // -- return --

  return {
    data,
    defaultRange,
    loadedRange,
    visibleRange,
    visibleDataBeginIndex,
    loading,
    error,
    setDefaultRange,
    applyVisibleRangeChange,
    applyPredefinedRange,
    refresh,
    resetToDefault,
  };
}
